import { checkOnlineStatus, OfflineError } from '../composables/useOnlineStatus'
import { getValidToken, silentRefresh, shouldRefreshToken, clearAccessToken } from './authService'

const API_BASE =
  import.meta.env.VITE_CLIENT_API_BASE || '/client/api' // Якщо змінна не задана, використовуємо відносний шлях.

console.log('[apiClient] API_BASE:', API_BASE)

// Скидаємо клієнтський стан токена і направляємо користувача на сторінку логіна.
function redirectToLogin({ reason, preserveRedirect = true } = {}) {
  clearAccessToken()

  // Сторінка /demo саме зараз логіниться сама. Жорсткий перехід на /login
  // обірвав би цей запит на півдорозі: 18.10.2026 у журналі видно
  // GET /api/demo-login = 499 і гостя, що лишився на формі входу. Токен уже
  // скинуто вище, тож фонові запити просто дочекаються демо-сесії.
  if (window.location.pathname === '/demo' || window.location.pathname.startsWith('/demo/')) {
    return
  }

  const queryParams = new URLSearchParams()
  if (preserveRedirect) {
    // Зберігаємо бажаний маршрут, щоб після логіна повернути користувача в потрібне місце.
    const currentPath = `${window.location.pathname}${window.location.search}${window.location.hash}`
    queryParams.set('redirect', currentPath)
  }
  if (reason) {
    queryParams.set('reason', reason)
  }

  const loginUrl = `/login${queryParams.toString() ? `?${queryParams.toString()}` : ''}`
  window.location.replace(loginUrl)
}

// Виконує fetch-запит з актуальним access токеном з пам'яті
async function rawFetch(endpoint, options = {}) {
  if (!checkOnlineStatus()) {
    throw new OfflineError()
  }

  const url = `${API_BASE}${endpoint}`
  const isFormData = options.body instanceof FormData
  const token = getValidToken()

  const headers = {
    ...(isFormData ? {} : { 'Content-Type': 'application/json' }),
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...(options.headers || {}),
  }

  try {
    return await fetch(url, {
      ...options,
      headers,
      credentials: 'include',
    })
  } catch (err) {
    if (!checkOnlineStatus() || err.message === 'Failed to fetch') {
      throw new OfflineError()
    }
    throw err
  }
}

// Розбирає тіло помилки з відповіді сервера
async function parseError(response) {
  let message = `HTTP ${response.status}`
  let errorPayload = null
  try {
    const text = await response.text()
    try {
      errorPayload = JSON.parse(text)
      message = errorPayload.message || errorPayload.error || message
    } catch {
      message = text || message
    }
  } catch {
    // body недоступне — лишаємо дефолтне повідомлення
  }
  return { message, errorPayload }
}

// Перевіряє, чи є помилка 401 з token_required (прострочений/відсутній токен)
function isTokenRequiredError(status, message, errorPayload) {
  return (
    status === 401 &&
    (message === 'token_required' ||
      errorPayload?.message === 'token_required' ||
      errorPayload?.error === 'token_required')
  )
}

// Endpoint'и, для яких НЕ треба пробувати silent refresh при 401 (вони самі і є refresh-ом)
const SKIP_AUTO_REFRESH = new Set(['/refresh-token', '/login', '/logout', '/demo-login', '/auth/impersonate'])

// Базовий HTTP-запит до клієнтського PHP-бекенда з обробкою статусів та автооновленням JWT.
export async function apiRequest(endpoint, options = {}) {
  // Якщо access токена немає у пам'яті (наприклад, після перезавантаження сторінки) —
  // одразу пробуємо обміняти refresh-cookie на свіжий токен. Це уникне зайвого 401.
  if (!getValidToken() && !SKIP_AUTO_REFRESH.has(endpoint)) {
    await silentRefresh()
  }

  let response = await rawFetch(endpoint, options)

  // При 401 пробуємо оновити токен і повторити запит один раз
  if (response.status === 401 && !SKIP_AUTO_REFRESH.has(endpoint)) {
    const { message, errorPayload } = await parseError(response)
    if (isTokenRequiredError(response.status, message, errorPayload)) {
      const refreshed = await silentRefresh()
      if (refreshed) {
        response = await rawFetch(endpoint, options)
      } else {
        redirectToLogin({ reason: 'token_required' })
        throw new Error(message)
      }
    }
  }

  if (!response.ok) {
    const { message, errorPayload } = await parseError(response)

    // Якщо після retry все одно 401 — перенаправляємо на логін
    if (isTokenRequiredError(response.status, message, errorPayload)) {
      redirectToLogin({ reason: 'token_required' })
    }

    if (
      response.status === 403 &&
      (message === 'insufficient_role' ||
        errorPayload?.message === 'insufficient_role' ||
        errorPayload?.error === 'insufficient_role')
    ) {
      redirectToLogin({ reason: 'insufficient_role', preserveRedirect: false })
    }

    // При блокуванні користувача зберігаємо статус і редиректимо на сторінку блокування
    if (response.status === 403 && errorPayload?.error === 'user_blocked') {
      localStorage.setItem(
        'client_billing_status',
        JSON.stringify({
          status: 'blocked',
          blockReason: errorPayload.blockReason || null,
          blockedAt: errorPayload.blockedAt || null,
        })
      )
      // Уже на сторінці блокування — не редиректимо, інакше кожен фоновий 403
      // (аватар, поллінги тривог/чату) перезавантажував би сторінку по колу
      if (window.location.pathname !== '/blocked') {
        window.location.replace('/blocked')
        return
      }
      const blockedErr = new Error(message)
      blockedErr.status = 403
      blockedErr.errorCode = 'user_blocked'
      blockedErr.errorPayload = errorPayload
      throw blockedErr
    }

    const err = new Error(message)
    err.status = response.status
    err.errorCode = errorPayload?.error ?? null
    err.errorPayload = errorPayload ?? null
    throw err
  }

  const data = await response.json()

  // Після успішного запиту проактивно оновлюємо токен, якщо залишилось мало часу
  if (!SKIP_AUTO_REFRESH.has(endpoint)) {
    if (shouldRefreshToken()) silentRefresh()
  }

  return data
}

// Реекспортуємо OfflineError для використання в компонентах
export { OfflineError }
